import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubjectsList } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import {
  transformerRegistrationBewareData,
  initialValues,
} from 'mutation/utils/transformer-registration-beware';
import ExitModal from 'mutation/components/shared/exit-modal';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { validationSchema } from 'mutation/constants/registration-beware-validation';
import Buttons from 'mutation/components/registration-beware/buttons';
import RegistrationBeware from 'mutation/components/registration-beware/form';
import { PersonVM, RegistrationBewareFormVM } from 'mutation/view-models/registration-beware';
import { toast } from 'react-toastify';
import { transformerRequestRegisterBewareData } from 'mutation/utils/transformer-request-register-beware';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { AnomalyCrumbs, BewareCrumbs } from 'mutation/constants/declaration-cohabitation-crumbs';
import PmiTitle from 'shell/components/shared/title';
import { SearchPerson } from 'shell/view-models/search-anpr';
import { useBus } from 'shell/hooks/use-bus';

interface RegistrationBewareProps {
  isBeware?: boolean;
  title: string;
}

const RegistrationBewarePage = ({
  isBeware,
  title,
}: RegistrationBewareProps): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);

  const [person, getPerson] = useHttpFetch<HttpResponse<SubjectsList>, RegistrationBewareFormVM>(
    `${__PERSON_API_URL__}/persona/registrazione-diffida/ricerca-persona`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerRegistrationBewareData,
    }
  );

  const registerBeware = useHttpClient(`${__PERSON_API_URL__}/persona/registrazione-diffida`, {
    options: {
      method: 'POST',
    },
  });

  const navigate = useNavigate();

  const handleSubmit = useEventCallback((values: RegistrationBewareFormVM) => {
    const data = transformerRequestRegisterBewareData(values);

    (registerBeware({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate('/mutazioni/');
    });
  });

  const handleExit = useEventCallback(() => {
    navigate('/mutazioni');
  });

  const handleOpenModal = useEventCallback(() => {
    setOpen(true);
  });

  const onSearch = useEventCallback((values?: PersonVM | null) => {
    const hasSearchBirthFilters = !!values?.birthChoiceDate && !!values?.birthDate;

    const data: SearchPerson = {
      datiRichiesta: !isBeware
        ? {
            ricercaNazionale: '0',
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: values?.fiscalCode || undefined,
        nome: values?.name || undefined,
        cognome: values?.surname || undefined,
        senzaCognome: values?.withoutSurname || !values?.surname ? '1' : undefined,
        senzaNome: values?.withoutName || !values?.name ? '1' : undefined,
      },
      datiNascita: hasSearchBirthFilters
        ? {
            senzaGiorno:
              !values.birthChoiceDate?.includes('day') && values.birthChoiceDate?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !values.birthChoiceDate?.includes('day') && !values.birthChoiceDate?.includes('month')
                ? '1'
                : undefined,
            dataEvento: values.birthDate?.toFormat('yyyy-MM-dd') || undefined,
          }
        : undefined,
    };

    return new Promise<RegistrationBewareFormVM>((resolve, reject) =>
      (getPerson({ data }) as ResponsePromise<HttpResponse<SubjectsList>, RegistrationBewareFormVM>)
        .then((response) => {
          const responseCasted = response as RegistrationBewareFormVM;
          resolve(responseCasted);
        })
        .catch(() => reject())
    );
  });

  const dispatch = useBus();

  return (
    <Fragment>
      <Breadcrumbs items={isBeware ? BewareCrumbs : AnomalyCrumbs} />
      <PmiTitle title={title} />
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onReset={() => {
          dispatch.emit('OPEN_CLOSE_SEARCH', true);
          dispatch.emit('RESET_ANOMALIES');
        }}
      >
        <Form>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <Buttons handleOpenModal={handleOpenModal} isBeware={isBeware} />
            </Grid>
            <Grid item xs={12} md={10}>
              <RegistrationBeware
                initialValues={initialValues}
                data={person.data as RegistrationBewareFormVM}
                isBeware={isBeware}
                onSearch={onSearch}
              />
            </Grid>
          </Grid>
          <ExitModal open={open} onClose={() => setOpen(false)} onConfirm={handleExit} />
        </Form>
      </Formik>
    </Fragment>
  );
};

RegistrationBewarePage.displayName = 'Registration Beware page component';

export default RegistrationBewarePage;
